import { useState, useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";
import OptimizedImage from "../components/OptimizedImage";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GalleryPage = () => {
  const { t, lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setItems(response.data.filter(item => item.category !== "team"));
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error("Error fetching gallery:", error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ["all", "slaughterhouse", "salami", "stables", "transport"];

  const filteredItems = activeCategory === "all" 
    ? items 
    : items.filter(item => item.category === activeCategory);

  // Placeholder images if no items in database
  const placeholderItems = [
    { id: "1", title_sq: "Objekti i Thertores", title_en: "Slaughterhouse Facility", image_url: "https://static.prod-images.emergentagent.com/jobs/a9df5f04-5208-4dad-bbe9-67be3549da0e/images/4d5bb229e854c39a8aef860aad8d30c580d29b5b7d6c67eba9999e0ba152a4f6.png", category: "slaughterhouse" },
    { id: "2", title_sq: "Linja e Ftohjes", title_en: "Cold Chain", image_url: "https://static.prod-images.emergentagent.com/jobs/a9df5f04-5208-4dad-bbe9-67be3549da0e/images/edbbb2f96e313cb12f1a3de1109ac3c4c8824b88df7cd41c1efd78ee3f971406.png", category: "slaughterhouse" },
    { id: "3", title_sq: "Flota e Transportit", title_en: "Transport Fleet", image_url: "https://static.prod-images.emergentagent.com/jobs/a9df5f04-5208-4dad-bbe9-67be3549da0e/images/c588716a6106a93ab45c619a021259cf761d3020b3c0bfed9bc040b756e10c87.png", category: "transport" },
    { id: "4", title_sq: "Sallameria", title_en: "Deli Factory", image_url: "https://images.pexels.com/photos/7163991/pexels-photo-7163991.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", category: "salami" },
    { id: "5", title_sq: "Magazina", title_en: "Warehouse", image_url: "https://images.unsplash.com/photo-1771531072574-af6ed6b954c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMGluZHVzdHJpYWwlMjB3YXJlaG91c2V8ZW58MHx8fHwxNzc1MDI5Mjg0fDA&ixlib=rb-4.1.0&q=85", category: "slaughterhouse" },
    { id: "6", title_sq: "Stalla", title_en: "Stables", image_url: "https://images.pexels.com/photos/2255459/pexels-photo-2255459.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", category: "stables" }
  ];

  const displayItems = items.length > 0 ? filteredItems : (activeCategory === "all" ? placeholderItems : placeholderItems.filter(p => p.category === activeCategory));

  return (
    <div className="pt-20" data-testid="gallery-page">
      {/* Header */}
      <section className="py-24 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">{t.nav.gallery}</p>
          <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-[#0A0A0A] mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {t.gallery.title}
          </h1>
          <p className="text-xl text-zinc-600 max-w-2xl">{t.gallery.subtitle}</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Categories */}
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wide text-zinc-500 mb-4">
                  {lang === "sq" ? "Kategoritë" : "Categories"}
                </h3>
                <nav className="space-y-1">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors ${
                        activeCategory === cat 
                          ? "bg-[#0A0A0A] text-white" 
                          : "text-zinc-600 hover:bg-zinc-100"
                      }`}
                    >
                      {t.gallery.categories[cat]}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </div>

          {/* Gallery Grid */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="text-center py-12 text-zinc-500">{t.common.loading}</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {displayItems.map((item) => (
                  <div key={item.id} className="group" data-testid="gallery-item">
                    <div className="relative aspect-[4/3] overflow-hidden bg-zinc-100">
                      <OptimizedImage
                        src={item.image_url}
                        alt={lang === "sq" ? item.title_sq : item.title_en}
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-end">
                        <div className="p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                          <h3 className="text-white font-medium">
                            {lang === "sq" ? item.title_sq : item.title_en}
                          </h3>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GalleryPage;
